import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Term from "../components/Term";
import InviteColleagueModal from "../components/InviteColleagueModal";
import PortalChrome from "../components/portal/PortalChrome";
import { CALENDLY_SUCCESS_TEAM_URL } from "../config/constants";
import {
    getProposal,
    getRatesForTier,
    patchProposalRiders,
    portalHeartbeat,
    portalLogout,
    startProposalApplication,
} from "../lib/api";
import { track } from "../lib/analytics";

import "./PortalProposal.css";

/* ------------------------------------------------------------------------ */
/* Rate-table logic — mirrors the prototype script block in portal.html but
/* fetches real rates from /api/rates instead of a hard-coded array.
/* ------------------------------------------------------------------------ */
const DISPLAY_AGES = [18, 22, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75];
const COVERAGE_TIERS = [
    { value: 25000, label: "$25K" },
    { value: 50000, label: "$50K" },
    { value: 75000, label: "$75K" },
    { value: 100000, label: "$100K" },
];
const RIDER_COSTS = { waiver: 0.30, adb: 0.15, dependent: 2.40 };
const SESSION_MS = 20 * 60 * 1000;
const WARNING_MS = 3 * 60 * 1000;
const HEARTBEAT_THROTTLE_MS = 30 * 1000;

function modeMultiplier(mode) {
    if (mode === "monthly") return 26 / 12;
    if (mode === "annual") return 26;
    return 1;
}

function riderSum(r) {
    return (r.waiver ? RIDER_COSTS.waiver : 0)
        + (r.adb ? RIDER_COSTS.adb : 0)
        + (r.dependent ? RIDER_COSTS.dependent : 0);
}

function fmtMoney(n) {
    if (!Number.isFinite(n)) return "—";
    return "$" + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function nearestRateByAge(rows, age) {
    if (!rows || !rows.length) return null;
    const exact = rows.find((r) => r.age === age);
    if (exact) return exact;
    let best = rows[0];
    for (const r of rows) {
        if (Math.abs(r.age - age) < Math.abs(best.age - age)) best = r;
    }
    return best;
}

/* ------------------------------------------------------------------------ */
/* PortalProposal page                                                      */
/* ------------------------------------------------------------------------ */
export default function PortalProposal() {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [proposal, setProposal] = useState(null);
    const [loadErr, setLoadErr] = useState(null);

    const [rates, setRates] = useState([]); // rows for current (tier, classification)
    const [ratesLoading, setRatesLoading] = useState(false);

    // Filter state
    const [coverage, setCoverage] = useState(50000);
    const [classification, setClassification] = useState("non_tobacco");
    const [mode, setMode] = useState("biweekly");

    // Rider elections (initialized from server response once loaded)
    const [riders, setRiders] = useState({ waiver: true, adb: false, dependent: false });

    // Expandable sections open-state
    const [openExpand, setOpenExpand] = useState({});

    // Session timer
    const [remainingMs, setRemainingMs] = useState(SESSION_MS);
    const [timedOut, setTimedOut] = useState(false);
    const lastHeartbeatRef = useRef(Date.now());
    const sessionStartRef = useRef(Date.now());

    // Start application submit lock
    const [startingApp, setStartingApp] = useState(false);

    // Colleague state (Phase 1.5c)
    const [colleagueModalOpen, setColleagueModalOpen] = useState(false);
    const [colleaguesInvited, setColleaguesInvited] = useState(0);
    const [colleaguesMax, setColleaguesMax] = useState(3);
    const [sessionRole, setSessionRole] = useState("primary");

    // Flash animation trigger for rate table / archetypes
    const [flashKey, setFlashKey] = useState(0);

    /* ----- Load proposal ----- */
    useEffect(() => {
        track("portal_proposal_view", { proposalId: id });
        let cancelled = false;
        (async () => {
            try {
                const data = await getProposal(id);
                if (cancelled) return;
                setProposal(data);
                const serverRiders = data.riderElections || {};
                setRiders({
                    waiver: serverRiders.waiver_of_premium !== false,
                    adb: !!serverRiders.accidental_death_benefit,
                    dependent: !!serverRiders.dependent_coverage,
                });
                setColleaguesInvited(data.colleaguesInvited || 0);
                setColleaguesMax(data.colleaguesMax || 3);
                setSessionRole(data.sessionRole || "primary");
                setLoading(false);
                sessionStartRef.current = Date.now();
            } catch (err) {
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    navigate(`/portal/login?next=${encodeURIComponent(`/portal/proposal/${id}`)}`);
                    return;
                }
                if (!cancelled) {
                    setLoadErr(err?.message || "Error");
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [id, navigate]);

    /* ----- Fetch rates whenever tier/classification changes ----- */
    useEffect(() => {
        if (!proposal) return;
        let cancelled = false;
        setRatesLoading(true);
        (async () => {
            try {
                const data = await getRatesForTier(coverage, classification);
                if (!cancelled) {
                    setRates(data.rows || []);
                    setFlashKey((k) => k + 1);
                }
            } catch (_) {
                if (!cancelled) setRates([]);
            } finally {
                if (!cancelled) setRatesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [coverage, classification, proposal]);

    /* ----- Reset session on user activity + heartbeat throttle ----- */
    const resetSession = useCallback(() => {
        sessionStartRef.current = Date.now();
        setRemainingMs(SESSION_MS);
        const since = Date.now() - lastHeartbeatRef.current;
        if (since >= HEARTBEAT_THROTTLE_MS) {
            lastHeartbeatRef.current = Date.now();
            portalHeartbeat(id).catch(() => { /* ignore — timer ticks client-side */ });
        }
    }, [id]);

    useEffect(() => {
        if (timedOut) return;
        const evts = ["click", "keydown", "scroll", "mousemove"];
        let throttle = null;
        const onActivity = () => {
            if (throttle) return;
            throttle = setTimeout(() => { throttle = null; }, 4000);
            resetSession();
        };
        evts.forEach((e) => document.addEventListener(e, onActivity, { passive: true }));
        return () => {
            evts.forEach((e) => document.removeEventListener(e, onActivity));
            if (throttle) clearTimeout(throttle);
        };
    }, [resetSession, timedOut]);

    /* ----- Countdown tick ----- */
    useEffect(() => {
        if (timedOut) return;
        const iv = setInterval(() => {
            const remaining = SESSION_MS - (Date.now() - sessionStartRef.current);
            if (remaining <= 0) {
                setRemainingMs(0);
                setTimedOut(true);
                track("portal_session_timeout", { proposalId: id });
                clearInterval(iv);
                return;
            }
            setRemainingMs(remaining);
        }, 1000);
        return () => clearInterval(iv);
    }, [timedOut, id]);

    /* ----- Rider toggle ----- */
    const onRiderToggle = useCallback(async (key) => {
        const nextVal = !riders[key];
        setRiders((r) => ({ ...r, [key]: nextVal }));
        track("portal_rider_toggle", { key, value: nextVal });
        const payload = {
            waiver_of_premium: key === "waiver" ? nextVal : riders.waiver,
            accidental_death_benefit: key === "adb" ? nextVal : riders.adb,
            dependent_coverage: key === "dependent" ? nextVal : riders.dependent,
        };
        // Always send the full snapshot so server stays in sync.
        payload[
            { waiver: "waiver_of_premium", adb: "accidental_death_benefit", dependent: "dependent_coverage" }[key]
        ] = nextVal;
        try {
            await patchProposalRiders(id, payload);
        } catch (_) {
            // silent — optimistic update stays for now
        }
        setFlashKey((k) => k + 1);
    }, [id, riders]);

    /* ----- Expand toggles ----- */
    const toggleExpand = (key) => {
        setOpenExpand((o) => {
            const next = { ...o, [key]: !o[key] };
            track("portal_expand_toggle", { key, open: next[key] });
            return next;
        });
    };

    /* ----- Sign out / timeout / start-application ----- */
    const onSignOut = async () => {
        track("portal_sign_out", { proposalId: id });
        // eslint-disable-next-line no-alert
        if (!window.confirm(t("portal.full.signOutConfirm"))) return;
        try { await portalLogout(); } catch (_) { /* ignore */ }
        navigate("/portal/login");
    };

    const onStartApp = async () => {
        if (startingApp) return;
        setStartingApp(true);
        track("portal_start_application_click", { proposalId: id });
        try {
            const res = await startProposalApplication(id);
            const url = res?.redirect_url || "/team/apply";
            navigate(url);
        } catch (e) {
            if (e?.response?.status === 403 && sessionRole === "colleague") {
                // colleague session — surface the 403 message if available
                track("colleague_click_start_blocked", { proposalId: id });
            }
            setStartingApp(false);
        }
    };

    const onTalkToSuccess = () => {
        track("portal_talk_to_success", { proposalId: id });
    };

    /* ----- Derived display values ----- */
    const mm = Math.max(0, Math.floor(remainingMs / 60000));
    const ss = Math.max(0, Math.floor((remainingMs % 60000) / 1000));
    const warning = remainingMs <= WARNING_MS;

    const pq = proposal?.preliminaryQuote || {};
    const company = proposal?.company || {};
    const employees = company.employee_count || 0;
    const stateName = company.parsed_state_name || "—";

    // Projected annual premium — prefer server value if present
    const projectedAnnual = pq.projected_annual_company_premium
        ?? (employees * 0.35 * (((rates.find((r) => r.age === 35)?.bi_weekly_premium || 0)
            + (rates.find((r) => r.age === 45)?.bi_weekly_premium || 0)) / 2) * 26);

    const dateStamp = useMemo(() => {
        const d = new Date();
        const m = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${m[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }, []);

    const mult = modeMultiplier(mode);
    const riderTotalPerPeriod = riderSum(riders) * mult;

    const riderCount = (riders.waiver ? 1 : 0) + (riders.adb ? 1 : 0) + (riders.dependent ? 1 : 0);
    const riderColHeader = riderCount === 0
        ? t("portal.full.rates.col.noRiders")
        : t("portal.full.rates.col.riderN", { count: riderCount });

    const modeLabel = {
        biweekly: t("portal.full.rates.mode.biweekly"),
        monthly: t("portal.full.rates.mode.monthly"),
        annual: t("portal.full.rates.mode.annual"),
    }[mode];
    const tobaccoLabel = classification === "tobacco"
        ? t("portal.full.rates.tobacco.yes")
        : t("portal.full.rates.tobacco.non");
    const coverageLabel = COVERAGE_TIERS.find((c) => c.value === coverage)?.label || "$50K";
    const tierMeta = `${coverageLabel} · ${tobaccoLabel} · ${modeLabel}`;

    /* ----- Sample archetypes ----- */
    const sampleYoung = nearestRateByAge(rates, 22);
    const sampleMedian = nearestRateByAge(rates, 38);
    const sampleOld = nearestRateByAge(rates, 58);

    const samplePremium = (row) => {
        if (!row) return "—";
        const base = (row.bi_weekly_premium || 0) * mult;
        return fmtMoney(base + riderTotalPerPeriod);
    };

    /* ----- Loading / error states ----- */
    if (loading) {
        return (
            <PortalChrome
                actorType="employer"
                signOutHref="/portal/login"
            >
                <div className="portal-proposal-page" data-testid="portal-loading">
                    <div className="portal-state">
                        <div className="mono">{t("portal.loading")}</div>
                    </div>
                </div>
            </PortalChrome>
        );
    }
    if (loadErr || !proposal) {
        return (
            <PortalChrome
                actorType="employer"
                signOutHref="/portal/login"
            >
                <div className="portal-proposal-page" data-testid="portal-error">
                    <div className="portal-state">
                        <h2>{t("portal.error.h2")}</h2>
                        <p>{t("portal.error.body")}</p>
                        <button
                            className="next-primary"
                            style={{ marginTop: "1.5rem", display: "inline-block" }}
                            onClick={() => navigate("/portal/login")}
                            data-testid="portal-error-cta"
                        >
                            {t("portal.error.cta")}
                        </button>
                    </div>
                </div>
            </PortalChrome>
        );
    }

    /* ----- Render ----- */
    const proposalHeaderAccessory = (
        <>
            <div className="portal-breadcrumb" data-testid="portal-breadcrumb">
                <button
                    type="button"
                    className="portal-breadcrumb-link"
                    onClick={() => navigate("/portal/employer/quotes")}
                    data-testid="portal-breadcrumb-portal"
                    title={t("portal.full.navPortalTooltip", { defaultValue: "View all my quotes" })}
                >
                    {t("portal.full.navPortal")}
                </button>
                <span className="sep">/</span>
                <span>{company.legal_name}</span>
                <span className="sep">/</span>
                <span className="current">{t("portal.full.navProposal")}</span>
            </div>
            <span
                className={`session-timer ${warning ? "warning" : ""}`}
                data-testid="portal-session-timer"
            >
                {timedOut
                    ? t("portal.full.sessionExpired")
                    : t("portal.full.sessionBar", {
                        mm: String(mm).padStart(2, "0"),
                        ss: String(ss).padStart(2, "0"),
                    })}
            </span>
        </>
    );

    return (
        <PortalChrome
            actorType="employer"
            displayName={company.legal_name}
            headerAccessory={proposalHeaderAccessory}
            onSignOut={onSignOut}
        >
            <div className="portal-proposal-page" data-testid="portal-proposal">
                <div className="proposal-wrap">
                {/* LEFT — proposal doc */}
                <div className="proposal-doc">
                    {/* COVER */}
                    <div className="cover">
                        <div className="small-mono">
                            {t("portal.full.coverKicker", { date: dateStamp })}
                        </div>
                        <h1>
                            {t("portal.full.coverH1a")} <em>{t("portal.full.coverH1b")}</em>
                        </h1>
                        <div className="company-name" data-testid="portal-company-name">
                            {company.legal_name}
                        </div>

                        <div className="cover-stats" data-testid="portal-cover-stats">
                            <div className="stat">
                                <div className="stat-label">{t("portal.full.stats.eligible")}</div>
                                <div className="stat-value" data-testid="portal-stat-employees">{employees}</div>
                                <div className="stat-sub">{t("portal.full.stats.eligibleSub")}</div>
                            </div>
                            <div className="stat">
                                <div className="stat-label">{t("portal.full.stats.cost")}</div>
                                <div className="stat-value coral">$0</div>
                                <div className="stat-sub">{t("portal.full.stats.costSub")}</div>
                            </div>
                            <div className="stat">
                                <div className="stat-label">{t("portal.full.stats.state")}</div>
                                <div className="stat-value">{stateName}</div>
                                <div className="stat-sub">{t("portal.full.stats.stateSub")}</div>
                            </div>
                            <div className="stat">
                                <div className="stat-label">{t("portal.full.stats.projected")}</div>
                                <div className="stat-value" data-testid="portal-stat-projected">
                                    ${Math.round(projectedAnnual).toLocaleString()}
                                </div>
                                <div className="stat-sub">{t("portal.full.stats.projectedSub")}</div>
                            </div>
                        </div>

                        <div className="precensus-note">
                            <strong>{t("portal.full.preCensusLabel")}</strong>
                            This proposal uses the MassMutual Group Whole Life{" "}
                            <Term termKey="rateTables">rate tables</Term> for your state.
                            Final premiums will be confirmed once you complete the application
                            and upload your team's census — typically within 2–3% of what you
                            see here, depending on your actual age distribution and
                            tobacco-use ratio.
                        </div>
                    </div>

                    {/* RATES */}
                    <div className="proposal-section">
                        <div className="section-label mono">{t("portal.full.rates.sectionLabel")}</div>
                        <h2>
                            {t("portal.full.rates.h2a")}{" "}
                            <em>{t("portal.full.rates.h2b")}</em>{" "}
                            {t("portal.full.rates.h2c")}
                        </h2>
                        <p className="intro">
                            All rates are{" "}
                            <Term termKey="lockedForLife">locked for life</Term>{" "}
                            at enrollment and never increase as employees age. Use the filters
                            in the table header to change coverage amount, tobacco status, and
                            how rates are displayed.
                        </p>

                        <div className="rate-table-wrap" key={`rw-${flashKey}`}>
                            <div className="rate-table-head recomputed">
                                <div className="rate-table-title">{t("portal.full.rates.tableTitle")}</div>
                                <div className="rate-table-meta" data-testid="portal-rate-meta">{tierMeta}</div>
                            </div>
                            <div className="table-filters">
                                <div className="filter-group">
                                    <div className="filter-label">{t("portal.full.rates.filterCoverage")}</div>
                                    <div className="filter-pills" role="group">
                                        {COVERAGE_TIERS.map((c) => (
                                            <button
                                                key={c.value}
                                                type="button"
                                                className={`filter-pill ${coverage === c.value ? "active" : ""}`}
                                                onClick={() => {
                                                    setCoverage(c.value);
                                                    track("portal_filter_coverage", { value: c.value });
                                                }}
                                                data-testid={`portal-coverage-${c.value}`}
                                            >
                                                {c.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="filter-group">
                                    <div className="filter-label">{t("portal.full.rates.filterTobacco")}</div>
                                    <div className="filter-pills" role="group">
                                        <button
                                            type="button"
                                            className={`filter-pill ${classification === "non_tobacco" ? "active" : ""}`}
                                            onClick={() => {
                                                setClassification("non_tobacco");
                                                track("portal_filter_tobacco", { value: "non_tobacco" });
                                            }}
                                            data-testid="portal-tobacco-non"
                                        >
                                            {t("portal.full.rates.tobacco.non")}
                                        </button>
                                        <button
                                            type="button"
                                            className={`filter-pill ${classification === "tobacco" ? "active" : ""}`}
                                            onClick={() => {
                                                setClassification("tobacco");
                                                track("portal_filter_tobacco", { value: "tobacco" });
                                            }}
                                            data-testid="portal-tobacco-yes"
                                        >
                                            {t("portal.full.rates.tobacco.yes")}
                                        </button>
                                    </div>
                                </div>
                                <div className="filter-group">
                                    <div className="filter-label">{t("portal.full.rates.filterMode")}</div>
                                    <select
                                        className="filter-select"
                                        value={mode}
                                        onChange={(e) => {
                                            setMode(e.target.value);
                                            track("portal_filter_mode", { value: e.target.value });
                                        }}
                                        data-testid="portal-mode-select"
                                    >
                                        <option value="biweekly">{t("portal.full.rates.mode.biweekly")}</option>
                                        <option value="monthly">{t("portal.full.rates.mode.monthly")}</option>
                                        <option value="annual">{t("portal.full.rates.mode.annual")}</option>
                                    </select>
                                </div>
                            </div>
                            <table className="rates" data-testid="portal-rate-table">
                                <thead>
                                    <tr>
                                        <th>{t("portal.full.rates.col.age")}</th>
                                        <th>{t("portal.full.rates.col.base")}</th>
                                        <th data-testid="portal-rate-col-riders">{riderColHeader}</th>
                                        <th>{t("portal.full.rates.col.annual")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {DISPLAY_AGES.map((age) => {
                                        const row = rates.find((r) => r.age === age);
                                        const base = row ? row.bi_weekly_premium * mult : null;
                                        const withRiders = base != null ? base + riderTotalPerPeriod : null;
                                        const annual = row ? row.bi_weekly_premium * 26 : null;
                                        return (
                                            <tr key={age} data-testid={`portal-rate-row-${age}`}>
                                                <td className="age">Age {age}</td>
                                                <td className="num">
                                                    {ratesLoading ? "…" : base != null ? fmtMoney(base) : "—"}
                                                </td>
                                                <td className="num">
                                                    {ratesLoading ? "…" : withRiders != null ? fmtMoney(withRiders) : "—"}
                                                </td>
                                                <td className="num" style={{ color: "var(--muted)" }}>
                                                    {ratesLoading ? "…" : annual != null ? fmtMoney(annual) : "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* SAMPLE ARCHETYPES */}
                    <div className="proposal-section">
                        <div className="section-label mono">
                            {t("portal.full.archetypes.sectionLabel")}
                        </div>
                        <h2>
                            {t("portal.full.archetypes.h2a")}{" "}
                            <em>{t("portal.full.archetypes.h2b")}</em>
                        </h2>
                        <p className="intro">
                            These show how the math works for employees at three different
                            career stages. Once you upload your team's census during the
                            application, these will populate with your actual youngest,
                            median, and oldest workers.{" "}
                            <Term termKey="guaranteedValues">Guaranteed values</Term> only;
                            no projected dividends.
                        </p>

                        <div className="samples" key={`s-${flashKey}`} data-testid="portal-archetypes">
                            <div className="sample recomputed">
                                <div className="sample-label mono">{t("portal.full.archetypes.young.label")}</div>
                                <div className="sample-name">
                                    {t("portal.full.archetypes.young.name")}
                                    <span>{t("portal.full.archetypes.young.sub")}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.premium")}</span>
                                    <span className="val" data-testid="sample-young-premium">{samplePremium(sampleYoung)}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.coverage")}</span>
                                    <span className="val">${coverage.toLocaleString()}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.cashValue")}</span>
                                    <span className="val coral">
                                        {sampleYoung?.guaranteed_cash_value
                                            ? "$" + sampleYoung.guaranteed_cash_value.toLocaleString()
                                            : "—"}
                                    </span>
                                </div>
                            </div>
                            <div className="sample recomputed">
                                <div className="sample-label mono">{t("portal.full.archetypes.median.label")}</div>
                                <div className="sample-name">
                                    {t("portal.full.archetypes.median.name")}
                                    <span>{t("portal.full.archetypes.median.sub")}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.premium")}</span>
                                    <span className="val" data-testid="sample-median-premium">{samplePremium(sampleMedian)}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.coverage")}</span>
                                    <span className="val">${coverage.toLocaleString()}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.cashValue")}</span>
                                    <span className="val coral">
                                        {sampleMedian?.guaranteed_cash_value
                                            ? "$" + sampleMedian.guaranteed_cash_value.toLocaleString()
                                            : "—"}
                                    </span>
                                </div>
                            </div>
                            <div className="sample recomputed">
                                <div className="sample-label mono">{t("portal.full.archetypes.old.label")}</div>
                                <div className="sample-name">
                                    {t("portal.full.archetypes.old.name")}
                                    <span>{t("portal.full.archetypes.old.sub")}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.premium")}</span>
                                    <span className="val" data-testid="sample-old-premium">{samplePremium(sampleOld)}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.coverage")}</span>
                                    <span className="val">${coverage.toLocaleString()}</span>
                                </div>
                                <div className="sample-row">
                                    <span className="lbl">{t("portal.full.archetypes.row.cashValueOld")}</span>
                                    <span className="val coral">
                                        {sampleOld?.guaranteed_cash_value
                                            ? "$" + sampleOld.guaranteed_cash_value.toLocaleString()
                                            : "—"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* EXPLORE PROMPT */}
                    <div className="explore-prompt">
                        {t("portal.full.explorePrompt")}
                    </div>

                    {/* EXPANDABLE SECTIONS */}
                    <div
                        className="proposal-section"
                        style={{ paddingBottom: "2rem", marginBottom: "3rem", borderBottom: "none" }}
                    >
                        <div className="section-label mono">{t("portal.full.expand.sectionLabel")}</div>

                        {/* Commitments */}
                        <ExpandSection
                            open={openExpand.commit}
                            onToggle={() => toggleExpand("commit")}
                            testid="expand-commit"
                            title={<>{t("portal.full.expand.commit.title_a")} <em>{t("portal.full.expand.commit.title_b")}</em></>}
                            preview={<>Four things. Deduct, remit, pass us new hires, tell us when someone leaves. Designed to stay light so the plan remains <Term termKey="erisaExemption">exempt from ERISA</Term>.</>}
                        >
                            <ul className="commitments">
                                {["1", "2", "3", "4"].map((i) => (
                                    <li key={i}>
                                        <div className="num mono">{`0${i}`}</div>
                                        <div className="text">
                                            {t(`portal.full.expand.commit.l${i}t`)}
                                            <span className="sub">{t(`portal.full.expand.commit.l${i}s`)}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </ExpandSection>

                        {/* Covers */}
                        <ExpandSection
                            open={openExpand.covers}
                            onToggle={() => toggleExpand("covers")}
                            testid="expand-covers"
                            title={<>{t("portal.full.expand.covers.title_a")} <em>{t("portal.full.expand.covers.title_b")}</em></>}
                            preview={t("portal.full.expand.covers.preview")}
                        >
                            <h4>{t("portal.full.expand.covers.core_h")}</h4>
                            <p>
                                The base policy pays the face amount — whatever coverage tier the
                                employee selected — to their named beneficiary if they die while
                                the policy is in force. This applies regardless of cause of death
                                after the two-year <Term termKey="contestabilityPeriod">contestability period</Term>.
                                No cause-of-death exclusions apply once the contestability period ends.
                            </p>
                            <h4>
                                {t("portal.full.expand.covers.waiver_h_a")}{" "}
                                <em>{t("portal.full.expand.covers.waiver_h_b")}</em>
                            </h4>
                            <p>{t("portal.full.expand.covers.waiver_p1")}</p>
                            <p><strong>Typical employee cost:</strong> about $0.30 extra per bi-weekly deduction.</p>
                            <h4>{t("portal.full.expand.covers.adb_h")}</h4>
                            <p>{t("portal.full.expand.covers.adb_p1")}</p>
                            <p><strong>Typical employee cost:</strong> about $0.15 extra per bi-weekly deduction.</p>
                            <h4>{t("portal.full.expand.covers.dep_h")}</h4>
                            <p>{t("portal.full.expand.covers.dep_p1")}</p>
                            <h4>{t("portal.full.expand.covers.chronic_h")}</h4>
                            <p dangerouslySetInnerHTML={{ __html: t("portal.full.expand.covers.chronic_p1") }} />
                            <p>{t("portal.full.expand.covers.chronic_p2")}</p>
                            <p><strong>Why this matters:</strong> most life insurance can only help an employee's family after they're gone. This coverage is different — it's designed to protect employees both in death and during severe illness.</p>
                            <h4>{t("portal.full.expand.covers.notcov_h")}</h4>
                            <p>{t("portal.full.expand.covers.notcov_p")}</p>
                        </ExpandSection>

                        {/* Enrollment */}
                        <ExpandSection
                            open={openExpand.enroll}
                            onToggle={() => toggleExpand("enroll")}
                            testid="expand-enroll"
                            title={<>{t("portal.full.expand.enroll.title_a")} <em>{t("portal.full.expand.enroll.title_b")}</em></>}
                            preview={t("portal.full.expand.enroll.preview")}
                        >
                            {[
                                ["day1_n", "day1_t", "day1_s"],
                                ["day23_n", "day23_t", "day23_s"],
                                ["day57_n", "day57_t", "day57_s"],
                                ["day7_n", "day7_t", "day7_s"],
                                ["days730_n", "days730_t", "days730_s"],
                                ["day31_n", "day31_t", "day31_s"],
                            ].map(([nK, tK, sK]) => (
                                <div className="expand-fact" key={nK}>
                                    <div className="expand-fact-number">{t(`portal.full.expand.enroll.${nK}`)}</div>
                                    <div className="expand-fact-text">
                                        <strong>{t(`portal.full.expand.enroll.${tK}`)}</strong>
                                        {t(`portal.full.expand.enroll.${sK}`)}
                                    </div>
                                </div>
                            ))}
                        </ExpandSection>

                        {/* Portability */}
                        <ExpandSection
                            open={openExpand.port}
                            onToggle={() => toggleExpand("port")}
                            testid="expand-portability"
                            title={<>{t("portal.full.expand.portability.title_a")} <em>{t("portal.full.expand.portability.title_b")}</em></>}
                            preview={t("portal.full.expand.portability.preview")}
                        >
                            <p>{t("portal.full.expand.portability.p1")}</p>
                            <p>{t("portal.full.expand.portability.p2")}</p>
                            <div className="expand-detail-grid">
                                {["1", "2", "3", "4"].map((i) => (
                                    <div className="expand-detail" key={i}>
                                        <h5>{t(`portal.full.expand.portability.d${i}h`)}</h5>
                                        <p>{t(`portal.full.expand.portability.d${i}p`)}</p>
                                    </div>
                                ))}
                            </div>
                            <p>{t("portal.full.expand.portability.p3")}</p>
                        </ExpandSection>

                        {/* MassMutual */}
                        <ExpandSection
                            open={openExpand.mass}
                            onToggle={() => toggleExpand("mass")}
                            testid="expand-mass"
                            title={<>{t("portal.full.expand.mass.title_a")} <em>{t("portal.full.expand.mass.title_b")}</em></>}
                            preview={t("portal.full.expand.mass.preview")}
                        >
                            <p>{t("portal.full.expand.mass.p1")}</p>
                            {["1", "2", "3", "4"].map((i) => (
                                <div className="expand-fact" key={i}>
                                    <div className="expand-fact-number">{t(`portal.full.expand.mass.f${i}n`)}</div>
                                    <div className="expand-fact-text">
                                        <strong>{t(`portal.full.expand.mass.f${i}t`)}</strong>
                                        {t(`portal.full.expand.mass.f${i}s`)}
                                    </div>
                                </div>
                            ))}
                            <p>{t("portal.full.expand.mass.p2")}</p>
                        </ExpandSection>

                        {/* ERISA */}
                        <ExpandSection
                            open={openExpand.erisa}
                            onToggle={() => toggleExpand("erisa")}
                            testid="expand-erisa"
                            title={<>{t("portal.full.expand.erisa.title_a")} <em>{t("portal.full.expand.erisa.title_b")}</em></>}
                            preview={t("portal.full.expand.erisa.preview")}
                        >
                            <h4>{t("portal.full.expand.erisa.h_safe")}</h4>
                            <p>{t("portal.full.expand.erisa.p1")}</p>
                            <ul>
                                <li>{t("portal.full.expand.erisa.l1")}</li>
                                <li>{t("portal.full.expand.erisa.l2")}</li>
                                <li>{t("portal.full.expand.erisa.l3")}</li>
                                <li>{t("portal.full.expand.erisa.l4")}</li>
                            </ul>
                            <p>{t("portal.full.expand.erisa.p2")}</p>
                            <h4>{t("portal.full.expand.erisa.h_state")}</h4>
                            <p>{t("portal.full.expand.erisa.p3")}</p>
                            <h4>{t("portal.full.expand.erisa.h_counsel")}</h4>
                            <p>{t("portal.full.expand.erisa.p4")}</p>
                        </ExpandSection>
                    </div>

                    {/* START APPLICATION BLOCK */}
                    <div className="next-step-block" data-testid="portal-start-app-block">
                        <div className="section-label mono">{t("portal.full.next.label")}</div>
                        <h2>
                            {t("portal.full.next.h2a")}{" "}
                            <em>{t("portal.full.next.h2b")}</em>
                        </h2>
                        <p className="pitch">
                            The application is where the proposal becomes real. You'll design
                            the plan in detail (rider elections, class structure, enrollment
                            window), upload your team's census, and digitally sign.{" "}
                            <strong>Takes about 15 minutes.</strong> After that, we send
                            personalized invitations to your eligible employees and your
                            program goes live on the effective date you choose.
                        </p>
                        <div className="next-actions">
                            <button
                                type="button"
                                className="next-primary"
                                onClick={onStartApp}
                                disabled={startingApp}
                                data-testid="portal-start-application"
                            >
                                {startingApp ? t("portal.full.next.primarySubmitting") : t("portal.full.next.primary")}
                            </button>
                            <a
                                className="next-secondary"
                                href={CALENDLY_SUCCESS_TEAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={onTalkToSuccess}
                                data-testid="portal-talk-to-success"
                            >
                                {t("portal.full.next.secondary")}
                            </a>
                        </div>
                        <p className="next-note">{t("portal.full.next.note")}</p>
                    </div>
                </div>

                {/* RIGHT — controls rail */}
                <aside className="controls-rail" data-testid="portal-controls-rail">
                    <div className="rail-section">
                        <div className="rail-label mono">{t("portal.full.rail.label")}</div>
                        <p className="rail-intro">
                            Choose which <Term termKey="rider">riders</Term> to include.
                            These become part of the plan your employees enroll in.
                        </p>

                        <button
                            type="button"
                            className={`toggle-row ${riders.waiver ? "on" : ""}`}
                            onClick={() => onRiderToggle("waiver")}
                            data-testid="rider-waiver"
                            aria-pressed={riders.waiver}
                        >
                            <div className="toggle-info">
                                <div className="toggle-name">{t("portal.full.rail.waiver_name")}</div>
                                <div className="toggle-desc">{t("portal.full.rail.waiver_desc")}</div>
                            </div>
                            <div className="toggle-switch" aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            className={`toggle-row ${riders.adb ? "on" : ""}`}
                            onClick={() => onRiderToggle("adb")}
                            data-testid="rider-adb"
                            aria-pressed={riders.adb}
                        >
                            <div className="toggle-info">
                                <div className="toggle-name">{t("portal.full.rail.adb_name")}</div>
                                <div className="toggle-desc">{t("portal.full.rail.adb_desc")}</div>
                            </div>
                            <div className="toggle-switch" aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            className={`toggle-row ${riders.dependent ? "on" : ""}`}
                            onClick={() => onRiderToggle("dependent")}
                            data-testid="rider-dependent"
                            aria-pressed={riders.dependent}
                        >
                            <div className="toggle-info">
                                <div className="toggle-name">{t("portal.full.rail.dep_name")}</div>
                                <div className="toggle-desc">{t("portal.full.rail.dep_desc")}</div>
                            </div>
                            <div className="toggle-switch" aria-hidden="true" />
                        </button>
                    </div>

                    <div className="rail-note">{t("portal.full.rail.note")}</div>

                    {/* Loop in a colleague */}
                    {sessionRole === "primary" && (
                        <div className="rail-section rail-colleague" data-testid="rail-colleague">
                            <div className="rail-label mono">{t("colleague.railLabel")}</div>
                            <p className="rail-intro">
                                {t("colleague.railIntro")}
                            </p>
                            <button
                                type="button"
                                className="rail-colleague-btn"
                                onClick={() => {
                                    track("colleague_invite_open", { proposalId: id });
                                    setColleagueModalOpen(true);
                                }}
                                disabled={colleaguesInvited >= colleaguesMax}
                                data-testid="open-colleague-modal"
                            >
                                {colleaguesInvited >= colleaguesMax
                                    ? t("colleague.railMaxReached")
                                    : t("colleague.railCta")}
                            </button>
                            <div className="rail-colleague-count">
                                {t("colleague.railSlots", { used: colleaguesInvited, max: colleaguesMax })}
                            </div>
                        </div>
                    )}

                    {sessionRole === "colleague" && (
                        <div
                            className="rail-note"
                            style={{ background: "var(--accent-soft)", borderLeftColor: "var(--accent)" }}
                            data-testid="colleague-session-note"
                        >
                            {t("colleague.viewerNote")}
                        </div>
                    )}
                </aside>
            </div>

            {/* SESSION TIMEOUT MODAL */}
            {timedOut && (
                <div className="timeout-overlay" role="alertdialog" aria-modal="true" data-testid="portal-timeout">
                    <div className="timeout-modal">
                        <h3>
                            {t("portal.full.timeout.h3a")}{" "}
                            <em>{t("portal.full.timeout.h3b")}</em>
                        </h3>
                        <p>{t("portal.full.timeout.p")}</p>
                        <button onClick={() => navigate("/portal/login")} data-testid="portal-timeout-cta">
                            {t("portal.full.timeout.cta")}
                        </button>
                    </div>
                </div>
            )}

            {/* COLLEAGUE INVITE MODAL */}
            <InviteColleagueModal
                open={colleagueModalOpen}
                onClose={() => setColleagueModalOpen(false)}
                proposalId={id}
                slotsUsed={colleaguesInvited}
                maxAllowed={colleaguesMax}
                role={sessionRole}
                onInvited={(newCount) => setColleaguesInvited(newCount)}
            />
            </div>
        </PortalChrome>
    );
}

/* ------------------------------------------------------------------------ */
/* ExpandSection — "click to discover" accordion                            */
/* ------------------------------------------------------------------------ */
function ExpandSection({ open, onToggle, testid, title, preview, children }) {
    return (
        <div className={`expand-section ${open ? "open" : ""}`} data-testid={testid}>
            <button className="expand-header" onClick={onToggle} type="button" aria-expanded={!!open}>
                <div>
                    <div className="expand-title">{title}</div>
                    <div className="expand-preview">{preview}</div>
                </div>
                <div className="expand-icon" aria-hidden="true" />
            </button>
            <div className="expand-body">{children}</div>
        </div>
    );
}
